import { useState, useRef } from "react";
import { DndContext, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { DRAG_SHAPES } from "@/data/exercises";
import { Check } from "lucide-react";

function Draggable({ shape, placed }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: shape.id,
    disabled: placed,
  });
  if (placed) return null;
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`drag-shape-${shape.id}`}
      style={{
        transform: CSS.Translate.toString(transform),
        backgroundColor: shape.color,
        touchAction: "none",
        zIndex: isDragging ? 50 : 1,
      }}
      className="w-24 h-24 rounded-full shadow-md border-4 border-white flex items-center justify-center text-white font-patient font-bold text-lg cursor-grab active:cursor-grabbing"
    >
      {shape.label}
    </button>
  );
}

function Slot({ shape, filled }) {
  const { setNodeRef, isOver } = useDroppable({ id: shape.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`drop-slot-${shape.id}`}
      className={`w-28 h-28 rounded-3xl border-4 border-dashed flex items-center justify-center transition-colors duration-200 ${
        filled ? "border-emerald-600 bg-emerald-50" : isOver ? "border-emerald-500 bg-emerald-50/60" : "border-gray-300 bg-white"
      }`}
      style={{ borderColor: filled ? shape.color : undefined }}
    >
      {filled ? (
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: shape.color }}>
          <Check className="w-8 h-8 text-white" strokeWidth={3} />
        </div>
      ) : (
        <span className="font-patient font-bold text-gray-500">{shape.label}</span>
      )}
    </div>
  );
}

export default function DragMatch({ onComplete }) {
  const [placed, setPlaced] = useState([]);
  const attempts = useRef(0);
  const start = useRef(Date.now());
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    attempts.current += 1;
    if (active.id === over.id && !placed.includes(active.id)) {
      const next = [...placed, active.id];
      setPlaced(next);
      if (next.length === DRAG_SHAPES.length) {
        const duration = Math.round((Date.now() - start.current) / 1000);
        const accuracy = Math.round((DRAG_SHAPES.length / Math.max(attempts.current, DRAG_SHAPES.length)) * 100);
        setTimeout(() => onComplete({ score: accuracy, accuracy, duration }), 600);
      }
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto" data-testid="dragmatch-game">
      <p className="text-center text-2xl font-patient font-bold text-gray-700 mb-10">
        Glissez chaque pastille vers le cadre de la même couleur
      </p>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex justify-center gap-8 mb-16 flex-wrap min-h-[6rem]">
          {DRAG_SHAPES.map((s) => (
            <Draggable key={s.id} shape={s} placed={placed.includes(s.id)} />
          ))}
        </div>
        <div className="flex justify-center gap-8 flex-wrap">
          {DRAG_SHAPES.map((s) => (
            <Slot key={s.id} shape={s} filled={placed.includes(s.id)} />
          ))}
        </div>
      </DndContext>
    </div>
  );
}
